import { useState } from 'react';
import dayjs, { Dayjs } from 'dayjs';
import { useTranslation } from 'react-i18next';

import { IPaginationParams } from 'services/types';
import { INotificationLogFilterParams } from 'services/GeneralLogs/NotificationLogService/types';
import { useGetAllNotificationLogsQuery, useGetNotificationLogsCountQuery } from 'services/GeneralLogs/NotificationLogService/hooks/useQuery';

export const useNotificationLogs = () => {
  const { t } = useTranslation();
  const [pagination, setPagination] = useState<IPaginationParams>({ pageNumber: 1, pageSize: 10 });
  const [filter, setFilter] = useState<INotificationLogFilterParams>({});

  const [selectedPlatforms, setSelectedPlatforms] = useState<string[]>([]);
  const [showSuccess, setShowSuccess] = useState<boolean>(false);
  const [showError, setShowError] = useState<boolean>(false);

  const [startDate, setStartDate] = useState<Dayjs | null>(null);
  const [endDate, setEndDate] = useState<Dayjs | null>(null);
  const [activeQuickFilter, setActiveQuickFilter] = useState<string>('todos');

  const isDateInvalid = startDate && endDate && startDate.isAfter(endDate, 'day');
  const dateErrorMessage = isDateInvalid
    ? t('notificationLogs.filters.invalidDateRange')
    : undefined;

  const { data: logs = [], isLoading, isFetching, refetch } = useGetAllNotificationLogsQuery(pagination, filter);
  const { data: totalCount = 0, refetch: refetchCount } = useGetNotificationLogsCountQuery(filter);

  const togglePlatform = (platformName: string) => {
    setSelectedPlatforms(prev => 
      prev.includes(platformName) ? prev.filter(p => p !== platformName) : [...prev, platformName]
    );
  };

  const handleQuickDateFilter = (preset: 'hoje' | '7d' | 'todos') => {
    setActiveQuickFilter(preset);
    let newStart = null;
    let newEnd = null;

    if (preset === 'hoje') {
      newStart = dayjs().startOf('day');
      newEnd = dayjs().endOf('day');
    } else if (preset === '7d') {
      newStart = dayjs().subtract(7, 'day').startOf('day');
      newEnd = dayjs().endOf('day');
    }

    setStartDate(newStart);
    setEndDate(newEnd);
    applyFilters(newStart, newEnd);
  };

  const applyFilters = (start: Dayjs | null = startDate, end: Dayjs | null = endDate) => {
    if (start && end && start.isAfter(end, 'day')) return;
    
    let isSuccessFilter: boolean | undefined = undefined;
    if (showSuccess && !showError) isSuccessFilter = true;
    if (!showSuccess && showError) isSuccessFilter = false;

    setFilter({
      platforms: selectedPlatforms.length > 0 ? selectedPlatforms : undefined,
      isSuccess: isSuccessFilter,
      startDate: start ? start.toISOString() : undefined,
      endDate: end ? end.toISOString() : undefined,
    });
    setPagination({ ...pagination, pageNumber: 1 });
  };

  const handleClearFilters = () => {
    setSelectedPlatforms([]);
    setShowSuccess(false);
    setShowError(false);
    setStartDate(null);
    setEndDate(null);
    setActiveQuickFilter('todos');
    setFilter({});
    setPagination({ pageNumber: 1, pageSize: pagination.pageSize });
  };

  const handleChangePage = (event: unknown, newPage: number) => {
    setPagination((prev) => ({ ...prev, pageNumber: newPage + 1 }));
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setPagination({ pageNumber: 1, pageSize: parseInt(event.target.value, 10) });
  };

  const handleRefresh = () => {
    refetch();
    refetchCount();
  };

  return {
    state: {
      logs, totalCount, isLoading, isFetching, pagination,
      filters: { selectedPlatforms, showSuccess, showError, startDate, endDate, activeQuickFilter },
      validation: { isDateInvalid, dateErrorMessage }
    },
    actions: {
      togglePlatform, handleQuickDateFilter, applyFilters, handleClearFilters,
      handleChangePage, handleChangeRowsPerPage, handleRefresh,
      setShowSuccess, setShowError, setStartDate, setEndDate, setActiveQuickFilter
    }
  };
};